import "./style.scss";

import type { IExtraConditions } from "components/Settings";
import { PortalType } from "contexts/portal.context";
import { useAbility } from "hooks/useAbility";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store/store";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";
interface NavBarSettingsProps {
    page: string;
    extraConditions?: IExtraConditions;
}

interface SettingList {
    groupLabel: string;
    /**
     * Authorized Account types
     */
    accountType?: "publisher" | "admin";
    /**
     * Settings subject for access control
     */
    scope?: string;
    /**
     * Extra Condition to show setting group
     */
    extraCondition?: boolean;
    children: {
        label: string;
        link: string;
        pageName: string;
        linkType?: "onClick" | "a";
        /**
         * Settings subject for access control
         */
        scope?: string;
        portal?: Exclude<TPortalType, "beneficiary">[];
        /**
         * Extra Condition to show setting group
         */
        extraCondition?: boolean;
        extraClass?: string;
    }[];
}

export const NavigationBarSettings = ({ page, extraConditions }: NavBarSettingsProps) => {
    const portalType = useContext(PortalType) as Exclude<TPortalType, "beneficiary">;
    const navigate = useNavigate();
    const [t] = useTranslation();
    const ability = useAbility();
    const { userData } = useAppSelector((state) => state.settings);
    const showForDemo = userData?.type === "admin" && userData?.email?.includes("crealo.app");

    const generalSettingsList: SettingList[] = [
        {
            groupLabel: "Settings.Profile",
            extraCondition: true,
            children: [
                {
                    label: "Settings.Account",
                    link: "/settings/account",
                    pageName: "account",
                },
            ],
        },
        {
            groupLabel: "Settings.Workspace",
            children: [
                {
                    label: "Settings.CompanyInfo",
                    link: "/settings/company",
                    pageName: "company",
                    scope: "CompanySettings",
                },
                {
                    label: "Settings.Brands",
                    link: "/settings/brand",
                    pageName: "brand",
                    scope: "BrandSettings",
                    extraCondition: extraConditions?.usesBrands,
                },
                {
                    label: "Settings.Member",
                    link: "/settings/members",
                    pageName: "members",
                    scope: "MemberSettings",
                },
                {
                    label: "Settings.ScopedNotifications",
                    link: "/settings/notifications",
                    pageName: "notifications",
                    scope: "NotificationSettings",
                    extraCondition: extraConditions?.usesScopedNotifications,
                },
                {
                    label: "Settings.Channels",
                    link: "/settings/channels",
                    pageName: "channels",
                    portal: ["book"],
                    scope: "ChannelSettings",
                },
                {
                    label: "Settings.FormatTypes",
                    link: "/settings/format-types",
                    pageName: "formatTypes",
                    portal: ["book"],
                    scope: "ChannelSettings",
                },
                {
                    label: "Settings.TypesService",
                    link: "/settings/services",
                    pageName: "services",
                    portal: ["press", "audiovisual"],
                    scope: "ServiceSettings",
                    extraCondition: extraConditions?.usesIRCEC,
                },
                {
                    label: "Settings.Export",
                    link: "/settings/export",
                    pageName: "export",
                    portal: ["book", "press", "audiovisual"],
                    scope: "ExportSettings",
                },
                {
                    label: "Settings.Currency",
                    link: "/settings/currency",
                    pageName: "currency",
                    portal: ["book"],
                    scope: "CurrencySettings",
                    extraCondition: extraConditions?.usesMultiCurrency,
                },
                {
                    label: "Settings.EDIInterforum.Title",
                    link: "/settings/edi-interforum",
                    pageName: "edi-interforum",
                    portal: ["book"],
                    scope: "ExportSettings", // EDI is part of export settings
                    extraCondition: extraConditions?.usesInterforumEDI,
                },
                {
                    label: "Settings.History",
                    link: "/settings/history",
                    pageName: "history",
                    scope: "HistorySettings",
                },
                {
                    label: "Settings.Billing",
                    link: "https://billing.stripe.com/p/login/14k9Cd2TrdY74VO9AA",
                    pageName: "billing",
                    linkType: "a",
                    scope: "BillingSettings",
                },
            ],
        },
        {
            groupLabel: "Settings.Customization",
            children: [
                {
                    label: "Settings.Sales",
                    link: "/settings/sales",
                    pageName: "sales",
                    portal: ["book"],
                    scope: "ChannelSettings",
                },
                {
                    label: "Settings.Statements",
                    link: "/settings/statements",
                    pageName: "statements",
                    portal: ["book", "press", "audiovisual"],
                    scope: "StatementSettings",
                },
                {
                    label: "Settings.PartnerStatements",
                    link: "/settings/partner-statements",
                    pageName: "partner-statements",
                    portal: ["book"],
                    scope: "PartnerStatementSettings",
                    extraCondition: extraConditions?.usesStatementTemplates,
                },
                {
                    label: "Settings.Metadata",
                    link: "/settings/metadata",
                    pageName: "metadata",
                    portal: ["book", "press", "audiovisual"],
                    extraCondition: extraConditions?.usesMetadata && extraConditions?.canAccessMetadata,
                },
                {
                    label: "Settings.BeneficiaryDocuments",
                    link: "/settings/beneficiary-documents",
                    pageName: "beneficiaryDocuments",
                    portal: ["book", "press", "audiovisual"],
                    scope: "CustomBeneficiarySettings",
                    extraCondition: extraConditions?.usesBeneficiaryDocuments,
                },
                {
                    label: "TermTemplates.TermTemplates",
                    link: "/settings/term-templates",
                    pageName: "terms",
                    portal: ["book", "press", "audiovisual"],
                    scope: "TermSettings",
                },
                {
                    label: "EmailSettings.Email",
                    link: "/settings/email",
                    pageName: "email",
                    scope: "EmailSettings",
                },
                {
                    label: "Settings.Contracts",
                    link: "/settings/contracts",
                    pageName: "contracts",
                    scope: "ContractSettings",
                    extraCondition: extraConditions?.usesAgreements,
                },
                {
                    label: "Settings.Accounting",
                    link: "/settings/accounting",
                    pageName: "accounting",
                    scope: "AccountingSettings",
                },
                {
                    label: "AdvanceProvision.Settings.Title",
                    link: "/settings/advance-provision",
                    pageName: "advance-provision",
                    portal: ["book"],
                    extraCondition: extraConditions?.canReadAdvanceProvision,
                },
                {
                    label: "Settings.AdvanceStatuses",
                    link: "/settings/advance-statuses",
                    pageName: "advance-statuses",
                    portal: ["book"],
                    extraCondition: extraConditions?.canReadAdvanceCustomStatus,
                },
                {
                    label: "AnalyticalCode.Title",
                    link: "/settings/analytical",
                    pageName: "analytical",
                    scope: "AnalyticalSettings",
                },
                {
                    label: "Settings.Docoon",
                    link: "/settings/docoon",
                    pageName: "docoon",
                    scope: "IntegrationSettings",
                    extraCondition: extraConditions?.hasDocoonIntegration,
                },
            ],
        },
        {
            groupLabel: "Settings.Advanced",
            children: [
                {
                    label: "Settings.Integrations",
                    link: "/settings/integrations",
                    pageName: "integrations",
                    scope: "IntegrationSettings",
                },
                {
                    label: "Settings.InvoicePercentages",
                    link: "/settings/invoice-percentages",
                    pageName: "invoicePercentages",
                    extraCondition: extraConditions?.usesBexio || showForDemo,
                    extraClass: "large",
                    scope: "IntegrationSettings", // Part of integrations
                },
                {
                    label: "Settings.API",
                    link: "/settings/api",
                    pageName: "api",
                    scope: "ApiKeySettings",
                },
                {
                    label: "Settings.Permissions",
                    link: "/settings/permissions",
                    pageName: "permissions",
                    scope: "PermissionSettings",
                    extraCondition: extraConditions?.usesCustomRoles,
                },
            ],
        },
    ];

    const urssafSettingList: SettingList[] = [
        {
            groupLabel: "Settings.Workspace",
            children: [
                {
                    label: "Settings.CompanyInfo",
                    link: "/settings/company",
                    pageName: "company",
                },
                {
                    label: "Settings.Member",
                    link: "/settings/members",
                    pageName: "members",
                },
                {
                    label: "Settings.Billing",
                    link: "https://billing.stripe.com/p/login/14k9Cd2TrdY74VO9AA",
                    pageName: "billing",
                    linkType: "a",
                },
            ],
        },
        {
            groupLabel: "Settings.Profile",
            children: [
                {
                    label: "Settings.Account",
                    link: "/settings/account",
                    pageName: "account",
                },
            ],
        },
    ];

    const settingsList = portalType === EPortalType.URSSAF ? urssafSettingList : generalSettingsList;

    const defaultPortals = ["book", "press", "audiovisual", "urssaf"] as Exclude<TPortalType, "beneficiary">[];

    const allowedSettingsList = settingsList
        .map((sttgGroup) => ({
            ...sttgGroup,
            children: sttgGroup.children.filter(({ scope, extraCondition = true, portal = defaultPortals }) => {
                const hasAccess = !scope || ability.can("access", scope);
                const matchesPortal = portal.includes(portalType);
                return hasAccess && matchesPortal && extraCondition;
            }),
        }))
        .filter(({ scope, extraCondition = true, children }) => {
            const hasAccess = !scope || ability.can("access", scope);
            return hasAccess && extraCondition && children.length > 0;
        });

    return (
        <div className="setting-navbar-wrapper">
            <div className="publisher-settings-navbar">
                <h2>{t("Settings.Settings")}</h2>
                <ul className="publisher-settings-navbar-list">
                    <div className="publisher-setting-navbar-list-group">
                        {/** Setting Group */}
                        {allowedSettingsList.map((settingGroup) => {
                            return (
                                <React.Fragment key={settingGroup.groupLabel}>
                                    <h3>{t(settingGroup.groupLabel)}</h3>

                                    {/** Setting Group Content */}
                                    {settingGroup.children.map((setting) => {
                                        const listElement =
                                            setting.linkType === "a" ? (
                                                <a
                                                    href={setting.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    key={setting.pageName}
                                                >
                                                    <li className={`publisher-settings-navbar-link`}>
                                                        {t(setting.label)}
                                                    </li>
                                                </a>
                                            ) : (
                                                <li
                                                    key={setting.pageName}
                                                    className={`publisher-settings-navbar-link ${
                                                        page == setting.pageName ? "active" : ""
                                                    } ${setting.extraClass ?? ""}`}
                                                    onClick={() => navigate(setting.link)}
                                                >
                                                    {t(setting.label)}
                                                </li>
                                            );

                                        return listElement;
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </ul>
            </div>
        </div>
    );
};

export default NavigationBarSettings;
